import {cn} from '../../lib/utils';
import {Icon} from '../Icon/Icon';
import {Button} from '../shadcn/button';
import {useDirection} from '../shadcn/direction';

import type {ReactNode} from 'react';

export interface CustomButtonConfig {
  /** Button label */
  label: ReactNode;
  /** Optional icon rendered before the label (hidden while loading) */
  icon?: ReactNode;
  /** Renders the button with the destructive variant */
  isDestructive?: boolean;
  /** Disables the button */
  isDisabled?: boolean;
  /** Shows a spinner in place of the icon and disables the button */
  isLoading?: boolean;
  /** Hides the button with a fade+scale-out animation while keeping it in the DOM */
  isHidden?: boolean;
  /** Click handler */
  onClick: () => void;
}

export interface ModalStepsFooterProps {
  /** Callback for the Next button. If omitted, the Next button is hidden. */
  onNext?: () => void;
  /** Label for the Next button. Defaults to "Next". */
  nextLabel?: ReactNode;
  /** Whether the Next button is disabled */
  nextDisabled?: boolean;
  /** Shows a spinner on the Next button and disables it */
  nextIsLoading?: boolean;
  /** Hide the chevron icon on the Next button */
  nextHideIcon?: boolean;
  /** Callback for the Back button. If omitted, the Back button is hidden. */
  onBack?: () => void;
  /** Label for the Back button. Defaults to "Back". */
  backLabel?: ReactNode;
  /** Whether the Back button is disabled */
  backDisabled?: boolean;
  /** Custom action buttons rendered between Back and Next. */
  customButtons?: CustomButtonConfig[];
  /** Additional class name on the root element */
  className?: string;
}

export function ModalStepsFooter({
  onNext,
  nextLabel = 'Next',
  nextDisabled = false,
  nextIsLoading = false,
  nextHideIcon = false,
  onBack,
  backLabel = 'Back',
  backDisabled = false,
  customButtons,
  className
}: ModalStepsFooterProps) {
  const direction = useDirection();
  const isRtl = direction === 'rtl';
  const backIconName = isRtl ? 'chevron-right' : 'chevron-left';
  const nextIconName = isRtl ? 'chevron-left' : 'chevron-right';

  return (
    <div
      data-slot="modal-steps-footer"
      className={cn(
        'flex w-full items-center justify-between border-t border-border pt-6',
        className
      )}
    >
      {onBack ? (
        <Button
          variant="secondary"
          onClick={onBack}
          disabled={backDisabled}
          icon={<Icon name={backIconName} width={20} height={20} />}
        >
          {backLabel}
        </Button>
      ) : (
        <div />
      )}

      <div className="flex items-center gap-2">
        {customButtons?.map((btn, i) => (
          <div
            key={i}
            inert={btn.isHidden || undefined}
            className={cn(
              'transition-all duration-150 ease-in-out',
              btn.isHidden ? 'pointer-events-none scale-90 opacity-0' : 'scale-100 opacity-100'
            )}
          >
            <Button
              variant="linkColor"
              destructive={btn.isDestructive}
              disabled={btn.isDisabled}
              isLoading={btn.isLoading}
              className="transition-opacity duration-150"
              onClick={btn.onClick}
              icon={btn.icon}
            >
              {btn.label}
            </Button>
          </div>
        ))}
        {onNext ? (
          <Button
            variant="primary"
            onClick={onNext}
            disabled={nextDisabled}
            isLoading={nextIsLoading}
            icon={!nextHideIcon ? <Icon name={nextIconName} width={20} height={20} /> : undefined}
            iconPosition="end"
          >
            {nextLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
